import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Box, AppBar, Toolbar, Container, Typography, Avatar, Button, Stack, Paper,
  Alert, CircularProgress, Checkbox, FormControlLabel, Divider,
} from "@mui/material";
import EventIcon from "@mui/icons-material/Event";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Swal from "sweetalert2";
import { AuthTabs } from "../Inscricao/CandidatoAuth";
import { candidatoLogado, getCandidato, logoutCandidato, previaPresenca, registrarPresenca } from "../../services/candidatoService";
import { instituicaoDe, instituicaoDoHost } from "../../config/instituicoes";

const fmtData = (d) => (d ? new Date(d).toLocaleDateString("pt-BR") : null);
const fmtDT = (d) => (d ? new Date(d).toLocaleString("pt-BR") : "—");

/**
 * Tela aberta pelo QR Code do encontro (/presenca/:token).
 * O inscrito entra na conta dele e confirma a presença — o servidor valida
 * o dia do encontro, a inscrição ativa e um único registro por participante.
 */
export default function PresencaCheckin() {
  const { token } = useParams();
  const [logado, setLogado] = useState(candidatoLogado());
  const [dados, setDados] = useState(null);     // null = carregando
  const [erro, setErro] = useState(null);
  const [sels, setSels] = useState([]);
  const [enviando, setEnviando] = useState(false);
  const [feito, setFeito] = useState(null);
  const cand = getCandidato();

  const carregar = useCallback(async () => {
    if (!logado) return;
    setErro(null);
    try {
      const { data } = await previaPresenca(token);
      setDados(data);
      setSels(data.Participantes.filter((p) => !p.Presente).map((p) => p.Id));
    } catch (e) {
      setDados(false);
      setErro(e?.response?.data?.mensagem || "Não foi possível ler este QR Code.");
    }
  }, [token, logado]);
  useEffect(() => { carregar(); }, [carregar]);

  const marca = dados?.Instituicao ? instituicaoDe(dados.Instituicao) : instituicaoDoHost();
  useEffect(() => { document.title = `Presença — ${marca.nome}`; }, [marca.nome]);

  const alternar = (id) =>
    setSels((arr) => (arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]));

  const confirmar = async () => {
    if (!sels.length) { Swal.fire("Atenção", "Selecione quem está presente.", "warning"); return; }
    setEnviando(true);
    try {
      const { data } = await registrarPresenca(token, sels);
      setFeito(data.mensagem);
      carregar();
    } catch (e) {
      Swal.fire("Não foi possível registrar", e?.response?.data?.mensagem || e.message, "error");
    } finally { setEnviando(false); }
  };

  const semPendencia = dados && dados.Participantes.every((p) => p.Presente);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <AppBar position="static" elevation={0} sx={{ background: marca.gradiente }}>
        <Toolbar>
          <Avatar sx={{ bgcolor: "rgba(255,255,255,0.15)", mr: 1.5 }}><EventIcon /></Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.1 }}>Registro de presença</Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)" }}>{marca.nome}</Typography>
          </Box>
          {logado && (
            <Button color="inherit" variant="outlined" size="small" sx={{ borderColor: "rgba(255,255,255,0.5)" }}
              onClick={() => { logoutCandidato(); setLogado(false); setDados(null); }}>
              Sair
            </Button>
          )}
        </Toolbar>
      </AppBar>

      <Container maxWidth="sm" sx={{ py: 3 }}>
        {!logado ? (
          <Paper sx={{ p: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>Entre com sua conta para registrar a presença.</Alert>
            <AuthTabs onOk={() => setLogado(true)} embutido />
          </Paper>
        ) : dados === null ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}><CircularProgress /></Box>
        ) : erro ? (
          <Alert severity="error">{erro}</Alert>
        ) : (
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="h6">{dados.EventoNome}</Typography>
            <Typography variant="body2" color="text.secondary">
              {dados.Encontro.Nome}{dados.Encontro.Data ? ` — ${fmtData(dados.Encontro.Data)}` : ""}
            </Typography>
            <Typography variant="caption" color="text.secondary">Conta: {cand?.Nome}</Typography>
            <Divider sx={{ my: 1.5 }} />

            {!dados.NoDia ? (
              <Alert severity="warning">
                Este QR Code é do encontro de <b>{fmtData(dados.Encontro.Data)}</b>. A presença só pode ser
                registrada nesse dia — procure a organização do evento.
              </Alert>
            ) : !dados.Participantes.length ? (
              <Alert severity="warning">Você não tem inscrição ativa neste evento.</Alert>
            ) : (
              <>
                {feito && <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 1.5 }}>{feito}</Alert>}
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Quem está presente:</Typography>
                {dados.Participantes.map((p) => (
                  <Box key={p.Id}>
                    <FormControlLabel
                      sx={{ display: "flex" }}
                      control={
                        <Checkbox checked={p.Presente || sels.includes(p.Id)} disabled={p.Presente}
                          onChange={() => alternar(p.Id)} />
                      }
                      label={
                        <span>
                          {p.Nome}{p.Turma ? ` — Turma ${p.Turma}` : ""}
                          {p.Presente && (
                            <Typography component="span" variant="caption" color="success.main">
                              {" "}• já registrada em {fmtDT(p.DtRegistro)}
                            </Typography>
                          )}
                        </span>
                      }
                    />
                  </Box>
                ))}

                {semPendencia ? (
                  <Alert severity="success" sx={{ mt: 1.5 }}>Presença de hoje já registrada. Nada mais a fazer.</Alert>
                ) : (
                  <Stack sx={{ mt: 2 }}>
                    <Button variant="contained" size="large" onClick={confirmar} disabled={enviando || !sels.length}>
                      {enviando ? "Registrando…" : "Confirmar presença"}
                    </Button>
                  </Stack>
                )}
              </>
            )}
          </Paper>
        )}
      </Container>
    </Box>
  );
}
